import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StaffFeesService, FeeAcademicYear, CollectedFeeMonth } from '../services/staff-fees.service';
import { StaffSessionService } from '../services/staff-session.service';
import { StaffFeesMonthlyNavState } from '../fees-monthly/staff-fees-monthly.component';

declare var customToastr: any;

interface MonthPoint {
  monthYear: string;
  label: string;
  amount: number;
  x: number;
  y: number;
}

const CRORE = 1_00_00_000;
const LAKH = 1_00_000;

@Component({
  selector: 'app-staff-fees',
  templateUrl: './staff-fees.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-fees.component.css']
})
export class StaffFeesComponent implements OnInit {
  currencySymbol = '';
  staffId: number | string | null = null;
  currentAcademicId: string | null = null;

  academicInfo: FeeAcademicYear[] = [];
  selectedYear: FeeAcademicYear | null = null;
  openAcademicSelection = false;

  feesData: CollectedFeeMonth[] = [];
  fetchingYears = true;
  fetchingData = true;

  openGraph = true;
  applyLateFees = false;

  readonly skeletonTiles = Array.from({ length: 16 }, () => ({}));

  constructor(
    private router: Router,
    private staffFeesService: StaffFeesService,
    private staffSession: StaffSessionService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const staffDetails = this.staffSession.getStaffDetails();
    const info = this.staffSession.getSchoolAndStaffInfo();
    this.staffId = staffDetails?.staffId ?? info?.StaffId ?? null;
    this.currencySymbol = info?.currencySymbol || staffDetails?.currencyType || '';
    // Staff's own "live" academic year — used only to show the Ongoing
    // badge, not to drive which year's data gets fetched.
    this.currentAcademicId = info?.pkAcademicId != null ? String(info.pkAcademicId) : null;

    this.loadAcademicInfo();
  }

  private async loadAcademicInfo() {
    if (!this.staffId) {
      this.fetchingYears = false;
      this.fetchingData = false;
      return;
    }
    this.fetchingYears = true;
    try {
      const response = await firstValueFrom(this.staffFeesService.getAcademicInfo(this.staffId));
      if (response?.status && response.body) {
        this.academicInfo = response.body.academicList || [];
        const current = this.academicInfo.find(y => y.InActive === 0) || this.academicInfo[0] || null;
        this.selectedYear = current;
        if (current) {
          await this.loadFeesData(current.pkAcademicId);
        } else {
          this.fetchingData = false;
        }
      } else {
        this.fetchingData = false;
        const message = await firstValueFrom(this.translate.get('somethingWentWrong'));
        customToastr(response?.message || message, 'error');
      }
    } catch {
      this.fetchingData = false;
      const message = await firstValueFrom(this.translate.get('somethingWentWrong'));
      customToastr(message, 'error');
    } finally {
      this.fetchingYears = false;
    }
  }

  private async loadFeesData(academicId: string) {
    if (!this.staffId) { return; }
    this.fetchingData = true;
    try {
      const response = await firstValueFrom(this.staffFeesService.getFeesInfo(this.staffId, academicId));
      this.feesData = response?.status ? (response.body?.collectedFeeData || []) : [];
    } catch {
      this.feesData = [];
      const message = await firstValueFrom(this.translate.get('somethingWentWrong'));
      customToastr(message, 'error');
    } finally {
      this.fetchingData = false;
    }
  }

  // Year switching is client-side selection + a fresh fetch for that year —
  // no mutation of academicInfo needed here since selectedYear alone drives
  // both the dropdown highlight and the "Ongoing" badge comparison.
  changeYear(year: FeeAcademicYear) {
    this.openAcademicSelection = false;
    if (this.selectedYear?.pkAcademicId === year.pkAcademicId) { return; }
    this.selectedYear = year;
    this.loadFeesData(year.pkAcademicId);
  }

  toggleAcademicSelection() {
    this.openAcademicSelection = !this.openAcademicSelection;
  }

  toggleGraph() {
    this.openGraph = !this.openGraph;
  }

  toggleLateFees() {
    this.applyLateFees = !this.applyLateFees;
  }

  get isOngoing(): boolean {
    return !!this.selectedYear && this.selectedYear.pkAcademicId === this.currentAcademicId;
  }

  private amountFor(month: CollectedFeeMonth): number {
    return this.applyLateFees ? month.collectedAmountWithLateFees : month.collectedAmount;
  }

  // Indian numbering shorthand: crore / lakh / thousand.
  private formatShorthand(value: number): string {
    if (!value) { return `${this.currencySymbol}0`; }
    if (value >= CRORE) { return `${+(value / CRORE).toFixed(1)}Cr`; }
    if (value >= LAKH) { return `${+(value / LAKH).toFixed(1)}L`; }
    return `${+(value / 1000).toFixed(1)}k`;
  }

  formatAmount(value: number): string {
    return `${this.currencySymbol}${(value || 0).toLocaleString('en-IN')}`;
  }

  private monthLabel(monthYear: string): string {
    const [month, year] = monthYear.split(' ');
    return month ? `${month.slice(0, 3)} ${year || ''}`.trim() : monthYear;
  }

  get maxValue(): number {
    return this.feesData.reduce((max, m) => Math.max(max, this.amountFor(m)), 0);
  }

  // 6 evenly-spaced Y-axis steps: 0, step, 2×step, ... max.
  get yAxisLabels(): string[] {
    const max = this.maxValue;
    if (!max) { return ['0', '0', '0', '0', '0', '0']; }
    const step = max / 5;
    return Array.from({ length: 6 }, (_, i) => this.formatShorthand(step * i)).reverse();
  }

  // A fixed per-month width keeps points evenly spaced and legible instead
  // of squeezing every month into one fixed-width viewBox — once the
  // computed width exceeds the card, .sf-chart-scroll scrolls horizontally.
  private readonly minPointSpacing = 90;
  readonly chartHeight = 200;

  get chartWidth(): number {
    const count = this.feesData.length;
    return Math.max(600, count * this.minPointSpacing);
  }

  get chartPoints(): MonthPoint[] {
    const max = this.maxValue || 1;
    const width = this.chartWidth;
    const height = this.chartHeight;
    const margin = 16;
    const count = this.feesData.length;
    const stepX = count > 1 ? (width - margin * 2) / (count - 1) : 0;
    return this.feesData.map((m, i) => {
      const amount = this.amountFor(m);
      return {
        monthYear: m.monthYear,
        label: this.monthLabel(m.monthYear),
        amount,
        x: margin + i * stepX,
        y: height - margin - (amount / max) * (height - margin * 2),
      };
    });
  }

  get chartLinePoints(): string {
    return this.chartPoints.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  }

  monthAmount(month: CollectedFeeMonth): number {
    return this.amountFor(month);
  }

  async openMonth(month: CollectedFeeMonth) {
    const amount = this.amountFor(month);
    if (amount <= 0) {
      const message = await firstValueFrom(this.translate.get('feeNotCollectedThisMonth'));
      customToastr(message, 'error');
      return;
    }
    if (!this.staffId || !this.selectedYear) { return; }
    const state: StaffFeesMonthlyNavState = {
      staffId: this.staffId,
      academicId: this.selectedYear.pkAcademicId,
      month: month.monthYear,
      currencySymbol: this.currencySymbol,
      enabledLateFees: this.applyLateFees,
      graphStatus: this.openGraph,
    };
    this.router.navigate(['/staff/fees-monthly'], { state });
  }
}
